import { Injectable } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';

import { BehaviorSubject, EMPTY, merge, switchMap, tap } from 'rxjs';

import { ERoutes } from 'src/app/core/enums/routes.enum';
import { ESvgIcons } from '../../../core/enums/svg-icons';
import { EInfoType } from '../../../core/enums/country-info-type.enum';
import { ICityResponse } from '../../../core/types/interfaces/city-response.interface';
import { IBreadcrumb } from '../../../core/types/interfaces/breadcrumb.interface';
import { ICountryResponse } from '../../../core/types/interfaces/country-response.interface';
import { IVoiceMapResponse } from '../../../core/types/interfaces/voice-map-response.interface';
import { OccasionGeniusResponse } from '../../../core/types/models/occasion-genius-response.model';
import { IOccasionGeniusResponse } from '../../../core/types/interfaces/occasion-genius-response.interface';

import { PackageService } from '../../../core/services/package.service';
import { CountryInfoService } from '../../../core/services/country-info.service';
import { OccasionGeniusService } from '../../../core/services/occasion-genius.service';
import { MetaTagsConfigService } from '../../../core/services/meta-tags-config.service';

import { IViatorCategories } from 'src/app/shared/used-components/viator/viator.component';
import { CountryResponse } from '../../../core/types/models/country-response.model';
import { CityResponse } from '../../../core/types/models/city-response.model';
import { CityInfo } from '../../../core/types/models/city-info.model';
import { VoiceMapResponse } from '../../../core/types/models/voice-map-response';

import moment from 'moment';

@Injectable()
export class ThingsToDoService {
  public readonly eSvgIcons = ESvgIcons;
  public readonly eInfoType = EInfoType;

  public menuOpened: boolean[] = [false, false, false];

  public readonly countryResponse$ = new BehaviorSubject<CountryResponse>(
    new CountryResponse(),
  );

  public readonly cityResponse$ = new BehaviorSubject<CityResponse>(
    new CityResponse(),
  );

  public readonly categoriesResponse$ = new BehaviorSubject<IViatorCategories>(
    {},
  );

  public readonly voiceMapResponse$ = new BehaviorSubject<VoiceMapResponse>(
    new VoiceMapResponse(),
  );

  public readonly occasionGeniusResponse$ =
    new BehaviorSubject<OccasionGeniusResponse>(new OccasionGeniusResponse());

  public readonly isFetching$ = new BehaviorSubject<boolean>(true);
  public readonly mapCoordinates$ = new BehaviorSubject<string>('');

  constructor(
    private countryInfoService: CountryInfoService,
    private packageService: PackageService,
    private metaTagsConfigService: MetaTagsConfigService,
    private occasionGeniusService: OccasionGeniusService,
    private r: Router,
  ) {}

  public fetchData(
    segments: UrlSegment[],
    callback: (breadcrumbs: IBreadcrumb[]) => void,
  ): void {
    const [city] = segments.map((s) => s.path);

    this.countryInfoService
      .getThingsToDoInCity(city.trim().replace(/ /g, '-'))
      .pipe(
        switchMap((cityResponse: ICityResponse) => {
          if (
            typeof cityResponse?.country_id === 'boolean' &&
            !cityResponse?.country_id
          ) {
            this.r.navigate([ERoutes.SLASH, ERoutes.ERROR]);
            return EMPTY;
          }

          this.initCity(cityResponse);

          const cityDetails: CityInfo = this.cityResponse$.value.cityInfo;
          const { link, name } = cityDetails.getFirstCity();
          const breadcrumbs = this.generateBreadcrumbs(link);
          this.setThingsToDoPageSeoTags(name);

          if (callback) {
            callback(breadcrumbs);
          }

          const [country] = breadcrumbs.map((s) => s.path);

          const voiceMap$ = this.countryInfoService
            .getVoiceMapInfoList(cityDetails.getFirstCity().place_id)
            .pipe(
              tap((voiceMapResponse) => {
                this.initVoiceMapResponse(voiceMapResponse);
              }),
            );

          const categories$ = this.packageService
            .getViatorEventsByCityId(cityDetails.getFirstCity().place_id)
            .pipe(
              tap((categoriesResponse) => {
                this.initCategories(categoriesResponse);
              }),
            );

          const countryInfo$ = this.countryInfoService
            .getCountryInfo(country)
            .pipe(
              tap((countryResponse) => {
                this.initCountry(countryResponse);
              }),
            );

          const occasionGenius$ = this.occasionGeniusService
            .getOccasionGeniusAll(cityDetails.getFirstCity().place_id, {
              from: moment().add(1, 'days').unix(),
              to: moment().add(1, 'days').add(1, 'year').unix(),
              offset: 0,
            })
            .pipe(
              tap((occasionGeniusResponse) => {
                this.initOccasionGeniusResponse(occasionGeniusResponse);
              }),
            );

          return merge(voiceMap$, categories$, countryInfo$, occasionGenius$);
        }),
      )
      .subscribe({
        error: (err) => {
          console.error('Error fetching data:', err);
        },
      });
  }

  public initOccasionGeniusResponse(response: IOccasionGeniusResponse): void {
    const occasionGeniusResponse = new OccasionGeniusResponse(response);
    this.occasionGeniusResponse$.next(occasionGeniusResponse);
  }

  public generateBreadcrumbs(link: any): IBreadcrumb[] {
    const segments = link
      .split('/')
      .filter((segment) => segment.length > 0)
      .map((segment) => new UrlSegment(segment, {}));

    let pathUrl = '';
    const pathArray = [];

    return segments.slice(0, 2).map((segment) => {
      pathUrl += `/${segment.path}`;
      pathArray.push(pathUrl);
      return { text: segment.path, path: pathUrl };
    });
  }

  private initCity(response: ICityResponse): void {
    const cityResponse = new CityResponse(response);
    this.cityResponse$.next(cityResponse);
    this.isFetching$.next(false);
  }

  private initCategories(response: IViatorCategories): void {
    this.categoriesResponse$.next(response);
  }

  private initVoiceMapResponse(response: IVoiceMapResponse): void {
    const voiceMapResponse = new VoiceMapResponse(response);
    this.voiceMapResponse$.next(voiceMapResponse);
  }

  private initCountry(response: ICountryResponse): void {
    const countryResponse = new CountryResponse(response);
    const countryDetails = countryResponse.countryInfo.getFirstCountry();
    this.countryResponse$.next(countryResponse);
    this.mapCoordinates$.next(countryDetails.coordinates);
  }

  private setThingsToDoPageSeoTags(name: string): void {
    if (name) {
      this.metaTagsConfigService.setThingsToDoSeoTags(name);
    }
  }
}
